import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { PageLoader } from '@/components/ui/page-loader'
import { invoicesService } from '@/services/invoices-service'
import { SendInvoiceModal } from '@/components/invoice/send-invoice-modal'
import { RecordPaymentModal } from '@/components/invoice/record-payment-modal'
import { CreateInvoiceModal } from '@/components/invoice/create-invoice-modal'
import type { InvoiceRecord, InvoicePaymentRecord } from '@/types'

function fmt(val: number): string {
  return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(str?: string | null): string {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function titleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const _user = useAuthStore((s) => s.user)
  void _user

  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null)
  const [allInvoices, setAllInvoices] = useState<InvoiceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSendModal, setShowSendModal] = useState(false)
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [converting, setConverting] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [issuingNextStage, setIssuingNextStage] = useState(false)

  // Editable draft fields
  const [editPaymentTerms, setEditPaymentTerms] = useState('')
  const [editTaxRate, setEditTaxRate] = useState(0)
  const [editNotes, setEditNotes] = useState('')

  // Stage percentage editing
  const [editingPercentages, setEditingPercentages] = useState(false)
  const [percentageEdits, setPercentageEdits] = useState<Record<string, number>>({})
  const [updatingPercent, setUpdatingPercent] = useState(false)


  // Fetch invoice on mount
  useEffect(() => {
    if (!id) {
      setError('Invoice ID is required')
      setLoading(false)
      return
    }

    let cancelled = false

    async function load() {
      try {
        const inv = await invoicesService.getById(id!)
        if (!cancelled) {
          setInvoice(inv)
          setError(null)

          // Load sibling invoices for staged view
          if (inv.proposalId) {
            try {
              const siblings = await invoicesService.getByProposal(inv.proposalId)
              // Only show non-void invoices with stage numbers, plus the current one
              const active = siblings.filter(s => s.status !== 'void')
              setAllInvoices(active)
            } catch {
              setAllInvoices([inv])
            }
          }
        }
      } catch (err) {
        console.error('Failed to load invoice:', err)
        if (!cancelled) {
          setError('Failed to load invoice. Please try again.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [id])

  // Sync editable fields when invoice loads
  useEffect(() => {
    if (invoice) {
      setEditPaymentTerms(invoice.paymentTerms)
      setEditTaxRate(invoice.taxRate)
      setEditNotes(invoice.notes || '')
    }
  }, [invoice?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Save a single field update (auto-save on blur)
  const saveField = async (field: string, value: unknown) => {
    if (!id || !invoice || invoice.status !== 'draft') return
    setSaving(true)
    try {
      const updated = await invoicesService.update(id, { [field]: value })
      setInvoice(updated)
    } catch (err) {
      console.error('Failed to save invoice field:', err)
    } finally {
      setSaving(false)
    }
  }

  const loadInvoice = async () => {
    if (!id) return
    try {
      const inv = await invoicesService.getById(id)
      setInvoice(inv)
      // Reload siblings too
      if (inv.proposalId) {
        const siblings = await invoicesService.getByProposal(inv.proposalId)
        setAllInvoices(siblings.filter(s => s.status !== 'void'))
      }
    } catch (err) {
      console.error('Failed to reload invoice:', err)
    }
  }

  // Handle stage mode change (with confirmation)
  const handleStageChange = async (newMode: string) => {
    if (!invoice || converting) return
    if (newMode === currentStageMode) return

    const label = newMode === 'one_time' ? 'One Time Invoice'
      : newMode === '2_stage' ? '2 Stage Invoice'
      : '3 Stage Invoice'

    const confirmed = window.confirm(
      `Switch to ${label}? This invoice will become Stage 1 and additional stage(s) will be created.`
    )
    if (!confirmed) return

    setConverting(true)
    try {
      if (newMode === 'one_time') {
        await invoicesService.convertToOneTime(invoice.proposalId)
      } else {
        const stageCount = newMode === '2_stage' ? 2 : 3
        await invoicesService.convertToStaged(invoice.id, stageCount as 2 | 3)
      }
      // Reload invoice data to show tabs/updates in place
      await loadInvoice()
    } catch (err) {
      console.error('Failed to convert invoice staging:', err)
      alert('Failed to change invoice staging. Please try again.')
    } finally {
      setConverting(false)
    }
  }

  // Handle entering edit mode
  const handleStartEditingPercentages = () => {
    const edits: Record<string, number> = {}
    stagedInvoices.forEach(inv => {
      edits[inv.id] = inv.stagePercent || 50
    })
    setPercentageEdits(edits)
    setEditingPercentages(true)
  }

  // Handle percentage change with auto-calculation for 3-stage
  const handlePercentageChange = (stageId: string, newPercent: number) => {
    const clamped = Math.min(100, Math.max(0, Number(newPercent) || 0))
    const updatedEdits = { ...percentageEdits, [stageId]: clamped }

    // For 2-stage, auto-calculate the other one
    if (stagedInvoices.length === 2) {
      const otherStage = stagedInvoices.find(s => s.id !== stageId)
      if (otherStage) {
        updatedEdits[otherStage.id] = 100 - clamped
      }
    }
    // For 3-stage, auto-calculate the third based on first two if they're being edited
    else if (stagedInvoices.length === 3) {
      const stages = stagedInvoices.sort((a, b) => (a.stageNumber || 0) - (b.stageNumber || 0))
      const [stage1, stage2, stage3] = stages

      if (stageId === stage1.id || stageId === stage2.id) {
        const s1 = stageId === stage1.id ? clamped : updatedEdits[stage1.id] || percentageEdits[stage1.id]
        const s2 = stageId === stage2.id ? clamped : updatedEdits[stage2.id] || percentageEdits[stage2.id]
        updatedEdits[stage3.id] = Math.max(0, 100 - s1 - s2)
      }
    }

    setPercentageEdits(updatedEdits)
  }

  // Handle saving percentage changes
  const handleSavePercentages = async () => {
    if (!invoice || !invoice.proposalId || updatingPercent) return

    // Validate percentages sum to 100
    const total = Object.values(percentageEdits).reduce((a, b) => a + b, 0)
    if (Math.abs(total - 100) > 0.01) {
      alert('Percentages must total 100%')
      return
    }

    setUpdatingPercent(true)
    try {
      const percentages = stagedInvoices.map(inv => ({
        invoiceId: inv.id,
        percent: percentageEdits[inv.id] || 50
      }))

      await invoicesService.updateStagePercentages(invoice.proposalId, percentages)
      await loadInvoice()
      setEditingPercentages(false)
      setPercentageEdits({})
    } catch (err) {
      console.error('Failed to update stage percentages:', err)
      alert('Failed to update percentages. Please try again.')
    } finally {
      setUpdatingPercent(false)
    }
  }

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!id || downloadingPdf) return
    setDownloadingPdf(true)
    try {
      const blob = await invoicesService.downloadPdf(id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${invoice?.invoiceNumber || 'invoice'}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download PDF:', err)
      alert('Failed to download PDF. Please try again.')
    } finally {
      setDownloadingPdf(false)
    }
  }

  // Handle void invoice
  const handleVoidInvoice = async () => {
    if (!id) return
    const confirmed = window.confirm('Are you sure you want to void this invoice?')
    if (!confirmed) return

    try {
      const updated = await invoicesService.voidInvoice(id, 'Voided by user')
      setInvoice(updated)
    } catch (err) {
      console.error('Failed to void invoice:', err)
      alert('Failed to void invoice. Please try again.')
    }
  }


  const handleIssueNextStage = async () => {
    if (!invoice?.proposalId || issuingNextStage) return
    const confirmed = window.confirm('Issue the next scheduled stage? This will assign an invoice number and send it to the client.')
    if (!confirmed) return
    setIssuingNextStage(true)
    try {
      await invoicesService.issueNextScheduledStage(invoice.proposalId)
      await loadInvoice()
    } catch (err) {
      console.error('Failed to issue next stage:', err)
      alert('Failed to issue next stage. Please try again.')
    } finally {
      setIssuingNextStage(false)
    }
  }

  if (loading) {
    return (
      <div className="inv-detail">
        <div className="est-list-head">
          <h1 className="est-list-title">Invoice</h1>
        </div>
        <PageLoader />
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="inv-detail">
        <div className="est-list-head">
          <h1 className="est-list-title">Invoice</h1>
        </div>
        <div className="est-list-error">
          {error || 'Invoice not found'}
        </div>
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button
            onClick={() => navigate('/invoices')}
            className="inv-detail-btn-secondary"
          >
            Back to Invoices
          </button>
        </div>
      </div>
    )
  }

  const isDraft = invoice.status === 'draft'
  const isPaid = invoice.status === 'paid'
  const isVoid = invoice.status === 'void'
  void invoice.status // isOverdue check happens via CSS
  const canSend = isDraft || invoice.status === 'sent'
  const canRecordPayment = !isPaid && !isVoid
  const canVoid = !isPaid && !isVoid

  const lineItems = Array.isArray(invoice.lineItemsSnapshot) ? invoice.lineItemsSnapshot : []
  const payments = invoice.payments || []

  // Staged invoice state
  const isStaged = !!invoice.stageNumber
  const stageCount = invoice.stageCount || null
  const currentStageMode = isStaged ? `${stageCount}_stage` : 'one_time'
  const stagedInvoices = allInvoices
    .filter(inv => inv.stageNumber && inv.status !== 'void')
    .sort((a, b) => (a.stageNumber || 0) - (b.stageNumber || 0))
  const showStageTabs = isStaged && stagedInvoices.length > 1

  // If any sibling stage has been sent/paid, lock down settings and percentages for all stages
  const anyStageSent = stagedInvoices.some(inv => inv.status !== 'draft')

  // Next scheduled stage (no invoice number yet, status draft)
  const nextScheduledStage = stagedInvoices.find(inv => inv.invoiceNumber === null && inv.status === 'draft')
  // Only show Issue Next Stage when viewing the most recently issued stage (last one with a number)
  const issuedStages = stagedInvoices.filter(inv => inv.invoiceNumber !== null)
  const lastIssuedStage = issuedStages[issuedStages.length - 1]
  const canIssueNextStage = !!nextScheduledStage && lastIssuedStage?.id === invoice.id

  // Find previous stage invoice (for soft warning)
  const currentStageNum = invoice.stageNumber || 0
  const previousStageInvoice = stagedInvoices.find(inv => inv.stageNumber === currentStageNum - 1)
  const previousStageUnpaid = previousStageInvoice && previousStageInvoice.status !== 'paid'

  return (
    <div className="inv-detail">
      {/* Page Header */}
      <div className="est-list-head">
        <div className="inv-detail-header">
          <h1>{invoice.invoiceNumber}</h1>
          <span className={`inv-status ${invoice.status}`}>
            {titleCase(invoice.status)}
          </span>
        </div>
      </div>

      {/* Stage Tabs (full-width, above the grid) */}
      {showStageTabs && (
        <div className="inv-stage-tabs-container">
          <div className="inv-stage-tabs">
            {stagedInvoices.map((stageInv) => (
              <button
                key={stageInv.id}
                className={`inv-stage-tab ${stageInv.id === invoice.id ? 'active' : ''}`}
                onClick={() => navigate(`/invoices/${stageInv.id}`)}
                disabled={editingPercentages}
              >
                <span className="inv-stage-tab-label">
                  {stageInv.stageLabel || `Stage ${stageInv.stageNumber}`}
                  {stageInv.invoiceNumber ? ` - ${stageInv.invoiceNumber}` : ' - Scheduled'}
                </span>
                {editingPercentages ? (
                  <input
                    type="number"
                    className="inv-stage-tab-input"
                    value={percentageEdits[stageInv.id] || 0}
                    onChange={(e) => handlePercentageChange(stageInv.id, Number(e.target.value))}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSavePercentages()
                      }
                      e.stopPropagation()
                    }}
                  />
                ) : (
                  <span className="inv-stage-tab-percent">{stageInv.stagePercent}%</span>
                )}
                <span className={`inv-status ${stageInv.status}`}>{titleCase(stageInv.status)}</span>
              </button>
            ))}
          </div>
          <div className="inv-stage-tabs-actions">
            {isDraft && !anyStageSent && (
              <button
                className={`inv-stage-tabs-edit-btn ${editingPercentages ? 'saving' : ''}`}
                onClick={() => {
                  if (editingPercentages) {
                    handleSavePercentages()
                  } else {
                    handleStartEditingPercentages()
                  }
                }}
                disabled={updatingPercent}
              >
                {editingPercentages ? 'Save' : 'Edit'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="inv-detail-grid">
        {/* LEFT COLUMN */}
        <div className="inv-detail-main">

          {/* Invoice Settings — editable for drafts, hidden once any stage has been sent */}
          {isDraft && !anyStageSent && (
            <div className="inv-detail-card inv-settings-bar">
              <div className="inv-settings-grid">
                <div className="inv-settings-field">
                  <div className="inv-info-label">Invoice Type</div>
                  <select
                    className="inv-modal-select"
                    value={currentStageMode}
                    onChange={(e) => handleStageChange(e.target.value)}
                    disabled={converting}
                  >
                    <option value="one_time">One Time Invoice</option>
                    <option value="2_stage">2 Stage Invoice</option>
                    <option value="3_stage">3 Stage Invoice</option>
                  </select>
                </div>
                <div className="inv-settings-field">
                  <div className="inv-info-label">Payment Terms</div>
                  <select
                    className="inv-modal-select"
                    value={editPaymentTerms}
                    onChange={(e) => {
                      setEditPaymentTerms(e.target.value)
                      saveField('paymentTerms', e.target.value)
                    }}
                  >
                    <option value="due_on_receipt">Due on Receipt</option>
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_45">Net 45</option>
                    <option value="net_60">Net 60</option>
                  </select>
                </div>
                <div className="inv-settings-field">
                  <div className="inv-info-label">Tax Rate</div>
                  <div className="inv-edit-tax-row">
                    <input
                      type="number"
                      className="inv-modal-input"
                      value={editTaxRate || ''}
                      min={0}
                      max={30}
                      step={0.01}
                      placeholder="0"
                      onChange={(e) => setEditTaxRate(Number(e.target.value) || 0)}
                      onBlur={() => { if (editTaxRate !== invoice.taxRate) saveField('taxRate', editTaxRate) }}
                    />
                    <span className="inv-edit-tax-suffix">%</span>
                    {(saving || converting) && <span className="inv-saving-indicator">{converting ? 'Converting...' : 'Saving...'}</span>}
                  </div>
                </div>
              </div>
            </div>
          )}


          {/* Previous stage warning */}
          {previousStageUnpaid && canSend && (
            <div className="inv-stage-warning">
              Note: {previousStageInvoice?.invoiceNumber} ({previousStageInvoice?.stageLabel}) has not been paid yet.
            </div>
          )}

          {/* Invoice Document Preview */}
          <div
            className="inv-doc"
            style={{ '--inv-brand': (invoice as any).brandingSnapshot?.primaryColor || undefined } as React.CSSProperties}
          >
            {/* Document Header — Company + Invoice Info */}
            <div className="inv-doc-header">
              <div className="inv-doc-company">
                {(invoice as any).brandingSnapshot?.logoUrl && (
                  <img src={(invoice as any).brandingSnapshot.logoUrl} alt="" className="inv-doc-logo" />
                )}
                <div className="inv-doc-company-name">
                  {(invoice as any).brandingSnapshot?.companyName || 'Company'}
                </div>
                {(() => {
                  const b = (invoice as any).brandingSnapshot || {}
                  const addr = [b.companyAddress, b.companyCity, b.companyState, b.companyZip].filter(Boolean).join(', ')
                  return (
                    <>
                      {addr && <div className="inv-doc-company-detail">{addr}</div>}
                      {b.companyPhone && <div className="inv-doc-company-detail">{b.companyPhone}{b.companyEmail ? ` | ${b.companyEmail}` : ''}</div>}
                      {!b.companyPhone && b.companyEmail && <div className="inv-doc-company-detail">{b.companyEmail}</div>}
                    </>
                  )
                })()}
              </div>
              <div className="inv-doc-meta">
                <div className="inv-doc-title">INVOICE</div>
                <div className="inv-doc-number">{invoice.invoiceNumber}</div>
                <div className="inv-doc-meta-row">
                  <span className="inv-doc-meta-label">Issued:</span>
                  <span>{fmtDate(invoice.issueDate)}</span>
                </div>
                <div className="inv-doc-meta-row">
                  <span className="inv-doc-meta-label">Due:</span>
                  <span>{fmtDate(invoice.dueDate)}</span>
                </div>
                <div className="inv-doc-meta-row">
                  <span className="inv-doc-meta-label">Terms:</span>
                  <span>{titleCase(invoice.paymentTerms)}</span>
                </div>
              </div>
            </div>

            {/* Bill To */}
            <div className="inv-doc-billto">
              <div className="inv-doc-section-label">Bill To:</div>
              <div className="inv-doc-client-name">{invoice.clientName}</div>
              {invoice.clientCompany && <div className="inv-doc-client-detail">{invoice.clientCompany}</div>}
              {invoice.jobAddress && <div className="inv-doc-client-detail">{invoice.jobAddress}</div>}
              {invoice.clientPhone && <div className="inv-doc-client-contact">{invoice.clientPhone}{invoice.clientEmail ? ` | ${invoice.clientEmail}` : ''}</div>}
            </div>

            {/* Staged Billing Context — shows this invoice is one portion of the project */}
            {invoice.billingSummary && (() => {
              const bs = invoice.billingSummary!
              return (
                <div className="inv-doc-billing-sched">
                  <div className="inv-billing-sched-head">
                    Staged Billing — Stage {bs.stageNumber} of {bs.stageCount}
                    {bs.stageLabel ? ` · ${bs.stageLabel}` : ''}
                    {bs.stagePercent ? ` (${bs.stagePercent}% of contract)` : ''}
                  </div>
                  <div className="inv-billing-sched-cells">
                    <div className="inv-billing-cell">
                      <span>Total Project Price</span><strong>{fmt(bs.projectTotal)}</strong>
                    </div>
                    <div className="inv-billing-cell">
                      <span>Previously Billed</span><strong>{fmt(bs.previouslyBilled)}</strong>
                    </div>
                    <div className="inv-billing-cell inv-billing-cell-accent">
                      <span>This Invoice</span><strong>{fmt(bs.thisStageAmountDue)}</strong>
                    </div>
                    <div className="inv-billing-cell">
                      <span>Remaining After This</span><strong>{fmt(bs.remainingBalance)}</strong>
                    </div>
                  </div>
                  <table className="inv-billing-sched-table">
                    <tbody>
                      {bs.stages.map((s) => (
                        <tr key={s.stageNumber} className={s.isCurrent ? 'inv-billing-current' : ''}>
                          <td>{s.stageLabel || `Stage ${s.stageNumber}`}{s.isCurrent ? ' (this invoice)' : ''}</td>
                          <td className="inv-billing-num">{s.invoiceNumber || 'Scheduled'}</td>
                          <td className="inv-billing-status">{titleCase(s.status)}</td>
                          <td className="inv-col-right inv-col-amount">{fmt(s.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bs.amountPaidToDate > 0 && (
                    <div className="inv-billing-paid-note">
                      Paid to date across all stages: <strong>{fmt(bs.amountPaidToDate)}</strong>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Job Summary — inline editable for drafts */}
            {isDraft ? (
              <div className="inv-doc-job-summary">
                <div className="inv-doc-section-label">Job Summary</div>
                <textarea
                  className="inv-doc-editable-textarea"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  onBlur={() => { if (editNotes !== (invoice.notes || '')) saveField('notes', editNotes) }}
                  placeholder="Describe the work performed..."
                  rows={3}
                />
              </div>
            ) : invoice.notes ? (
              <div className="inv-doc-job-summary">
                <div className="inv-doc-section-label">Job Summary</div>
                <div className="inv-doc-text">{invoice.notes}</div>
              </div>
            ) : null}

            {/* Service Provided Table */}
            {lineItems.length > 0 && (
              <div className="inv-doc-services">
                <div className="inv-doc-section-label">Service Provided</div>
                {lineItems[0]?.isSummary ? (
                  /* Category summary format (new invoices) */
                  <table className="inv-service-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="inv-col-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 1 ? 'inv-service-row-alt' : ''}>
                          <td>{item.description}</td>
                          <td className="inv-col-right inv-col-amount">{fmt(item.lineTotal || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  /* Granular line items format (legacy invoices) */
                  <table className="inv-service-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Product / Services</th>
                        <th className="inv-col-right">Qty</th>
                        <th className="inv-col-right">Unit Price</th>
                        <th className="inv-col-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item: any, idx: number) => (
                        <tr key={idx} className={idx % 2 === 1 ? 'inv-service-row-alt' : ''}>
                          <td>{item.description}</td>
                          <td>{titleCase(item.category || 'other')}</td>
                          <td className="inv-col-right">{item.quantity}</td>
                          <td className="inv-col-right">{fmt(item.rate || 0)}</td>
                          <td className="inv-col-right inv-col-amount">{fmt(item.lineTotal || (item.quantity * item.rate) || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="inv-doc-totals">
              <div className="inv-doc-totals-row">
                <span>Subtotal</span>
                <span>{fmt(invoice.subtotal)}</span>
              </div>
              {invoice.taxRate > 0 && (
                <div className="inv-doc-totals-row">
                  <span>Tax ({invoice.taxRate}%)</span>
                  <span>{fmt(invoice.taxAmount)}</span>
                </div>
              )}
              {invoice.discountAmount > 0 && (
                <div className="inv-doc-totals-row">
                  <span>Discount</span>
                  <span>-{fmt(invoice.discountAmount)}</span>
                </div>
              )}
              <div className="inv-doc-totals-row inv-doc-totals-total">
                <span>Total</span>
                <span>{fmt(invoice.total)}</span>
              </div>
            </div>

            {/* Amount Due Banner */}
            {invoice.amountDue > 0 ? (
              <div className="inv-doc-amount-due">
                <div className="inv-doc-amount-due-label">Amount Due</div>
                <div className="inv-doc-amount-due-value">{fmt(invoice.amountDue)}</div>
              </div>
            ) : isPaid ? (
              <div className="inv-doc-paid-banner">Paid in Full</div>
            ) : null}

            {/* Payment Terms & Remit To */}
            <div className="inv-doc-payment-info">
              <div className="inv-doc-payment-col">
                <div className="inv-doc-section-label">Payment Terms</div>
                <div className="inv-doc-payment-terms-text">
                  {titleCase(invoice.paymentTerms)}
                </div>
                <div className="inv-doc-payment-due">
                  Due by {fmtDate(invoice.dueDate)}
                </div>
              </div>
              <div className="inv-doc-payment-col">
                <div className="inv-doc-section-label">Send Payment To</div>
                {(() => {
                  const b = (invoice as any).brandingSnapshot || {}
                  return (
                    <>
                      <div className="inv-doc-remit-name">{b.companyName || ''}</div>
                      {b.companyAddress && <div className="inv-doc-remit-detail">{b.companyAddress}</div>}
                      {(b.companyCity || b.companyState || b.companyZip) && (
                        <div className="inv-doc-remit-detail">
                          {[b.companyCity, b.companyState].filter(Boolean).join(', ')}{b.companyZip ? ` ${b.companyZip}` : ''}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            </div>

            {/* Footer */}
            <div className="inv-doc-footer">Thank you for your business</div>

            {saving && <div className="inv-saving-indicator">Saving...</div>}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="inv-detail-sidebar">
          {/* Actions Card */}
          <div className="inv-detail-card">
            <h3>Actions</h3>
            <div className="inv-detail-actions">
              {canSend && (
                <button
                  onClick={() => setShowSendModal(true)}
                  className="inv-detail-btn-send"
                >
                  {invoice.status === 'sent' ? 'Resend Invoice' : 'Send Invoice'}
                </button>
              )}

              {canIssueNextStage && (
                <button
                  onClick={handleIssueNextStage}
                  className="inv-detail-btn-send"
                  disabled={issuingNextStage}
                >
                  {issuingNextStage ? 'Issuing...' : `Issue ${nextScheduledStage?.stageLabel || 'Next Stage'}`}
                </button>
              )}

              {canRecordPayment && (
                <button
                  onClick={() => setShowRecordPaymentModal(true)}
                  className="inv-detail-btn-payment"
                >
                  Record Payment
                </button>
              )}

              <button
                onClick={handleDownloadPdf}
                className="inv-detail-btn-pdf"
                disabled={downloadingPdf}
              >
                {downloadingPdf ? (
                  <>
                    <span className="prop-send-spinner inv-btn-spinner" />
                    Generating PDF...
                  </>
                ) : (
                  'Download PDF'
                )}
              </button>

              {canVoid && (
                <button
                  onClick={handleVoidInvoice}
                  className="inv-detail-btn-void"
                >
                  Void Invoice
                </button>
              )}

              {isVoid && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inv-detail-btn-send"
                >
                  Recreate Invoice
                </button>
              )}
            </div>
          </div>

          {/* Payment History */}
          {payments.length > 0 && (
            <div className="inv-detail-card">
              <h3>Payment History</h3>
              <div className="inv-payment-timeline">
                {payments.map((payment: InvoicePaymentRecord) => {
                  const methodLabel = titleCase(payment.method)
                  const detail = payment.reference
                    ? `${methodLabel} ending in ${payment.reference}`
                    : methodLabel

                  return (
                    <div key={payment.id} className="inv-payment-item">
                      <div className="inv-payment-info">
                        <div className="inv-payment-headline">
                          {fmt(payment.amount)} paid on {fmtDate(payment.paidAt)}
                        </div>
                        <div className="inv-payment-detail">{detail}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Related Proposal Link */}
          {invoice.proposalId && (
            <div className="inv-detail-card inv-related-link">
              <h3>Related</h3>
              <button
                onClick={() => navigate(`/proposals/${invoice.proposalId}`)}
                className="inv-related-btn"
              >
                <span className="inv-related-icon">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M5 9h6M5 11.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                </span>
                <span>View Proposal</span>
                <span className="inv-related-arrow">&rarr;</span>
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Modals */}
      <SendInvoiceModal
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        clientEmail={invoice.clientEmail}
        open={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSent={() => {
          setShowSendModal(false)
          loadInvoice()
        }}
      />

      <RecordPaymentModal
        invoiceId={invoice.id}
        invoiceNumber={invoice.invoiceNumber}
        amountDue={invoice.amountDue}
        open={showRecordPaymentModal}
        onClose={() => setShowRecordPaymentModal(false)}
        onRecorded={() => {
          setShowRecordPaymentModal(false)
          loadInvoice()
        }}
      />

      <CreateInvoiceModal
        proposalId={invoice.proposalId}
        proposalTitle={invoice.clientName}
        estimatedValue={invoice.subtotal}
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(newInvoiceId) => {
          setShowCreateModal(false)
          navigate(`/invoices/${newInvoiceId}`)
        }}
      />
    </div>
  )
}
